import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  TemplateRef,
  afterNextRender,
  computed,
  contentChild,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { SharedModule } from '@shared/shared.module';
import { HdChartMatchNameFilterComponent, hdNormalizeChartName } from './hd-chart-match-name-filter.component';
import { HdChartMatchScopeToggleComponent } from './hd-chart-match-scope-toggle.component';
import { HdChartSearchContextService } from './hd-chart-search-context.service';
import { hdChartListItemIcon } from './hd-chart-type-metadata';
import { hdRevealWhileGrowing } from './hd-reveal-while-growing';

/** Laadtoestand van een matchlijst; `idle` = nog niet gezocht (de lijst toont dan niets). */
export type HdChartMatchListStatus = 'idle' | 'loading' | 'loaded' | 'error';

/** Eén treffer: de chart plus wat de aanroeper erbij toont (via de meta-template). */
export interface HdChartMatchListEntry {
  readonly item: PlatformChartListItemDto;
}

export interface HdChartMatchListState<T extends HdChartMatchListEntry = HdChartMatchListEntry> {
  readonly status: HdChartMatchListStatus;
  readonly items: readonly T[];
}

export const HD_CHART_MATCH_LIST_IDLE: HdChartMatchListState<never> = { status: 'idle', items: new Array<never>() };

export interface HdChartMatchListLabels {
  readonly loading: string;
  readonly empty: string;
  readonly error: string;
}

/**
 * Gedeelde weergave van een lijst bibliotheek-charts die bij een zoektocht horen
 * (omgeving in Basis, profiel/kruis in Cycles, poort-info, Locks & Keys): laden,
 * fout, leeg of klikbare treffers met het chart-icoon (eigen icoon, cyclus of
 * charttype) en de naam, per {@link pageSize} met de gedeelde pager eronder.
 * Wat er rechts van de naam staat (plek-chips, profiel, poortactivaties,
 * lock/key-relaties) levert de aanroeper als template:
 *
 *   <hd-chart-match-list [state]="..." [labels]="..." (open)="...">
 *     <span matchListHead>…</span>   (optioneel: rechts in de kopregel)
 *     <ng-template let-entry>…</ng-template>
 *   </hd-chart-match-list>
 *
 * Overal hetzelfde gedrag:
 * - kopregel met de scope-toggle (bibliotheek / relaties) op de chart-pagina en,
 *   bij meer treffers dan één pagina, een zoekvak op chartnaam (client-side over
 *   alle pagina's: de zoektocht haalt alle treffers op, de lijst pagineert zelf);
 * - tijdens een nieuwe zoektocht blijven de vorige treffers gedimd staan (geen
 *   hoogtesprong of flits), tenzij {@link resetKey} wijzigde;
 * - bij openen scrolt het paneel mee zodat de groeiende lijst in beeld komt.
 */
@Component({
  selector: 'hd-chart-match-list',
  standalone: true,
  imports: [NgTemplateOutlet, SharedModule, HdChartMatchNameFilterComponent, HdChartMatchScopeToggleComponent],
  templateUrl: './hd-chart-match-list.component.html',
  styleUrl: './hd-chart-match-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartMatchListComponent<T extends HdChartMatchListEntry = HdChartMatchListEntry> {
  readonly state = input.required<HdChartMatchListState<T>>();
  readonly labels = input.required<HdChartMatchListLabels>();
  /** Gemarkeerde treffers (bv. sterke of wederzijdse matches) krijgen een amber accent. */
  readonly highlight = input<(entry: T) => boolean>(() => false);

  /** Treffers per pagina; bij meer treffers verschijnt de gedeelde pager onder de lijst. */
  readonly pageSize = input(10);
  /**
   * Wijzigt dit (bv. andere poort of chart), dan tonen we tijdens het laden niet
   * de vorige treffers: die horen bij iets anders.
   */
  readonly resetKey = input<unknown>(null);
  /** Bij openen de lijst in beeld scrollen; uit wanneer de aanroeper een grotere sectie toont. */
  readonly revealOnOpen = input(true);

  /** Klik op een treffer: de aanroeper opent die chart. */
  readonly open = output<PlatformChartListItemDto>();

  private readonly context = inject(HdChartSearchContextService);

  /** Scope-toggle enkel waar een chart-pagina de setting kan wijzigen. */
  protected readonly scopeToggleAvailable = computed(() => this.context.matchScopeSetter() != null);

  protected readonly page = signal(1);

  /** Laatst geladen treffers (+ hun reset-sleutel): blijven gedimd staan tijdens een nieuwe zoektocht. */
  private readonly lastLoaded = signal<{ readonly key: unknown; readonly items: readonly T[] }>({ key: null, items: [] });

  private readonly rememberLoaded = effect(() => {
    const state = this.state();
    const key = this.resetKey();
    untracked(() => {
      if (state.status === 'loaded') {
        this.lastLoaded.set({ key, items: state.items });
      } else if (this.lastLoaded().key !== key) {
        this.lastLoaded.set({ key, items: [] });
      }
    });
  });

  /**
   * Een nieuwe zoektocht loopt (of start zo): de vorige treffers blijven staan,
   * gedimd en met de laadmelding erover, zodat de lijst niet wegspringt.
   */
  protected readonly showStale = computed(() => {
    const status = this.state().status;
    const last = this.lastLoaded();
    return (status === 'loading' || status === 'idle') && last.items.length > 0 && last.key === this.resetKey();
  });

  private readonly displayItems = computed(() => (this.showStale() ? this.lastLoaded().items : this.state().items));

  /** Zoekvak op chartnaam (client-side, over alle pagina's). */
  protected readonly nameFilter = signal('');

  /** Enkel bij meer treffers dan één pagina (of terwijl er gefilterd wordt). */
  protected readonly showNameFilter = computed(() =>
    this.displayItems().length > this.pageSize() || this.nameFilter() !== '');

  protected readonly filteredItems = computed(() => {
    const query = hdNormalizeChartName(this.nameFilter());
    const items = this.displayItems();
    return query ? items.filter(entry => hdNormalizeChartName(entry.item.name ?? '').includes(query)) : items;
  });

  /** Er wordt gefilterd en geen enkele treffer past. */
  protected readonly noNameMatches = computed(() =>
    this.nameFilter() !== '' && this.displayItems().length > 0 && this.filteredItems().length === 0);

  /** Een nieuwe lijst (andere zoektocht, sortering, filter of naam) begint weer op pagina 1. */
  private readonly resetPage = effect(() => {
    this.state();
    this.nameFilter();
    untracked(() => this.page.set(1));
  });

  protected readonly pagedItems = computed(() => {
    const size = this.pageSize();
    const start = (this.page() - 1) * size;
    return this.filteredItems().slice(start, start + size);
  });

  protected readonly showPager = computed(() => this.filteredItems().length > this.pageSize());

  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      if (!this.revealOnOpen()) return;
      const stop = hdRevealWhileGrowing(host);
      destroyRef.onDestroy(stop);
    });
  }

  protected readonly metaTemplate = contentChild<TemplateRef<{ $implicit: T }>>(TemplateRef);

  protected iconClass(entry: T): string {
    return `fas fa-${hdChartListItemIcon(entry.item)}`;
  }

  protected trackEntry(entry: T): string {
    return entry.item.id ?? entry.item.name ?? '';
  }
}
